<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue"

const props = defineProps({
  members: {
    type: Array,
    required: true
  },
  initialSelectedIds: {
    type: Array,
    required: true
  },
  noResultsText: {
    type: String,
    required: true
  },
  removeText: {
    type: String,
    required: true
  },
  searchLabel: {
    type: String,
    required: true
  },
  searchPlaceholder: {
    type: String,
    required: true
  }
})

const root = ref(null)
const query = ref("")
const isOpen = ref(false)
const selectedIds = ref(new Set(props.initialSelectedIds.map((id) => String(id))))

const selectedMembers = computed(() => {
  return props.members.filter((member) => selectedIds.value.has(String(member.id)))
})

const availableMembers = computed(() => {
  return props.members.filter((member) => !selectedIds.value.has(String(member.id)))
})

const filteredMembers = computed(() => {
  const normalizedQuery = query.value.trim().toLowerCase()
  return availableMembers.value.filter((member) => member.searchText.includes(normalizedQuery))
})

function open() {
  isOpen.value = true
}

function close() {
  isOpen.value = false
}

function add(member) {
  selectedIds.value = new Set([...selectedIds.value, String(member.id)])
  query.value = ""
  open()
}

function remove(member) {
  const nextSelectedIds = new Set(selectedIds.value)
  nextSelectedIds.delete(String(member.id))
  selectedIds.value = nextSelectedIds
}

function closeOnOutsideClick(event) {
  if (!root.value?.contains(event.target)) close()
}

function removeLabel(member) {
  return `${props.removeText} ${member.name}`
}

onMounted(() => {
  document.addEventListener("click", closeOnOutsideClick)
})

onBeforeUnmount(() => {
  document.removeEventListener("click", closeOnOutsideClick)
})
</script>

<template>
  <div ref="root">
    <div>
      <input
        v-for="member in selectedMembers"
        :key="member.id"
        type="hidden"
        name="household[member_ids][]"
        :value="member.id"
      >
    </div>

    <div class="mb-3 flex flex-wrap gap-2">
      <span
        v-for="member in selectedMembers"
        :key="member.id"
        class="inline-flex items-center gap-2 rounded-full bg-secondary/10 px-3 py-2 text-sm font-semibold text-primary"
      >
        <span>{{ member.name }}</span>
        <button
          type="button"
          class="rounded-full px-1 text-primary/70 hover:bg-secondary/20 hover:text-primary"
          :aria-label="removeLabel(member)"
          @click="remove(member)"
        >
          x
        </button>
      </span>
    </div>

    <div class="relative">
      <label for="household_member_search" class="sr-only">{{ searchLabel }}</label>
      <input
        id="household_member_search"
        v-model="query"
        type="search"
        class="input w-full rounded-md border-base-300 bg-base-100"
        :placeholder="searchPlaceholder"
        autocomplete="off"
        @focus="open"
        @input="open"
        @keydown.esc="close"
      >

      <div
        v-if="isOpen"
        class="absolute z-20 mt-2 max-h-72 w-full overflow-y-auto rounded-lg border border-base-300 bg-base-100 shadow-lg"
      >
        <button
          v-for="member in filteredMembers"
          :key="member.id"
          type="button"
          class="block w-full px-4 py-3 text-left hover:bg-secondary/10"
          @mousedown.prevent="add(member)"
        >
          <span class="block font-semibold text-primary">{{ member.name }}</span>
          <span class="block text-sm text-neutral">{{ member.contact }}</span>
        </button>

        <p v-if="filteredMembers.length === 0" class="px-4 py-3 text-sm text-neutral">
          {{ noResultsText }}
        </p>
      </div>
    </div>
  </div>
</template>
